import React from "react"
import PropTypes from "prop-types"

import { getCompiler } from "utils/MarkdownCompile"
import { TextLink } from "./atoms/links/TextLink"
import { ButtonPrimary } from "./atoms/buttons/ButtonPrimary"
import { AvaCheckoutButton } from "./molecules/AvaCheckoutButton"
import styled from "styled-components"
import { ZendeskTextLink } from "./atoms/links/ZendeskTextLink"
import { isZendeskLink } from "carrot-api/HelpCenterClient"
import { reportErrorMessage } from "../../utils/ErrorReporting"
import { FormattedMessage } from "react-intl"

const StyledOl = styled.ol`
  margin: 16px 0px 16px -24px;
  padding-left: 40px;
  list-style: decimal;
`

const StyledUl = styled.ul`
  margin: 16px 0px 16px -24px;
  padding-left: 40px;
`

const StyledTable = styled.table`
  border-collapse: collapse;
  width: 100%;
  text-align: left;
  margin: 48px 0px 48px 0px;
`

const StyledTd = styled.td`
  padding: 16px;
  border: 1px solid #ccc;
  line-height: 150%;
`

const StyledTh = styled.th`
  padding: 16px;
  border: 1px solid #ccc;
  font-weight: 500;
  line-height: 150%;
`

const elements = {
  h1({ children }) {
    return <h1 className="publico-headline b normal lh-title mt5 black-90">{children}</h1>
  },
  h2({ children }) {
    return <h2 className="b gotham black-80 mb2 mt4 f4">{children}</h2>
  },
  h3({ children }) {
    return <h3 className="b gotham black-80 mb2 mt4 f5">{children}</h3>
  },
  h4({ children }) {
    return <h4 className="b gotham black-80 mb2 mt4 f6">{children}</h4>
  },
  hr() {
    return <hr className="w5-ns w4 mv4 center b--black-05" />
  },
  ol({ children }) {
    return <StyledOl>{children}</StyledOl>
  },
  ul({ children }) {
    return <StyledUl>{children}</StyledUl>
  },
  li({ children }) {
    return <li className="gotham lh-copy mv3">{children}</li>
  },
  p({ children }) {
    return <p className="lh-copy gotham black-80 mt0 mb3">{children}</p>
  },
  a({ href, title, target = "blank", children }) {
    if (!href) {
      reportErrorMessage(`Empty href found in Markdown with title ${title} and children ${JSON.stringify(children)}`)
    }
    if (isZendeskLink(href)) {
      return (
        <ZendeskTextLink target={target} title={title} to={href}>
          {children}
        </ZendeskTextLink>
      )
    }

    return (
      <TextLink target={target} title={title} to={href}>
        {children}
      </TextLink>
    )
  },
  table({ children }) {
    return <StyledTable>{children}</StyledTable>
  },
  th({ children }) {
    return <StyledTh>{children}</StyledTh>
  },
  td({ children }) {
    return <StyledTd>{children}</StyledTd>
  },
  blockquote({ children }) {
    return <blockquote>{children}</blockquote>
  },
  strong({ children }) {
    return <strong className="b">{children}</strong>
  },
  em({ children }) {
    return <em className="i">{children}</em>
  }
}
const logComponentRendered = (componentName) => {
  return reportErrorMessage(
    `The ${componentName} within default markdown  was rendered. Please refer to https://carrotfertility.atlassian.net/wiki/spaces/HIPPO/pages/2333638754/Default+Markdown+Sentry+Runbook for further instructions`
  )
}

const components = {
  Ava() {
    logComponentRendered("Ava")
    return (
      <>
        <AvaCheckoutButton ButtonComponent={ButtonPrimary} />
        <br />
      </>
    )
  },
  Chat() {
    logComponentRendered("Chat")
    return (
      <>
        <ButtonPrimary to="/talk-to-carrot/">
          <FormattedMessage defaultMessage="Chat with an expert" />
        </ButtonPrimary>
        <br />
      </>
    )
  },
  BookTime() {
    logComponentRendered("BookTime")
    return (
      <ButtonPrimary to="/talk-to-carrot">
        <FormattedMessage defaultMessage="Book time with an expert" />
      </ButtonPrimary>
    )
  },
  ReadMore() {
    logComponentRendered("ReadMore")
    return (
      <TextLink to="/read">
        <FormattedMessage defaultMessage="Read more" />
      </TextLink>
    )
  },
  BankInfo() {
    logComponentRendered("BankInfo")
    return (
      <TextLink to="/account">
        <FormattedMessage defaultMessage="To update your banking details, head to your profile." />
      </TextLink>
    )
  }
}

const md = getCompiler(elements, components)

const DefaultMarkdown = ({ additionalClasses, source }) => (
  <div className={`m0-first-last-child ${additionalClasses ? additionalClasses : ""}`}>{md(source)}</div>
)

DefaultMarkdown.propTypes = {
  source: PropTypes.string.isRequired,
  additionalClasses: PropTypes.string
}
export { elements, components }

export default DefaultMarkdown
